/**
 * Shared Components - Main Exports
 */

// =============================================================================
// ERROR HANDLING
// =============================================================================

export {
  ErrorBoundary,
  ErrorBoundaryWrapper,
} from './error/ErrorBoundary';

// export {
//   GlobalErrorHandler,
//   reportGlobalError,
//   withErrorReporting,
//   withGlobalErrorHandling,
// } from './error/GlobalErrorHandler';

export { ErrorDashboard } from './error/ErrorDashboard';
export { ErrorProvider, useErrorContext } from './error/ErrorContext';
export type { TrackedError, ErrorSeverity } from './error/ErrorContext';
export { EllipsisErrorBoundary } from './error/EllipsisErrorBoundary';

// =============================================================================
// TABLES
// =============================================================================

export { EnhancedTable } from './tables/EnhancedTable';
export type { TableDensity } from './tables/EnhancedTable';
export { VirtualizedTable } from './tables/VirtualizedTable';
export { CardToolbar, useCardTools } from './tables/CardToolbar';
export type { CardToolAction, CardToolGroup } from './tables/CardToolbar';

// =============================================================================
// FEEDBACK
// =============================================================================

export { SafeSpinner, TradingSpinner } from './feedback/SafeSpinner';
export { EnterpriseSpinner } from './feedback/EnterpriseSpinner';
export { FeedbackState } from './feedback/FeedbackState';
export type {
  FeedbackStateProps,
  FeedbackStateAction,
  FeedbackStateVariant,
} from './feedback/FeedbackState';
export { SafeLoadingOverlay } from './feedback/SafeLoadingOverlay';
export {
  ConnectionStatus,
  type ConnectionStatusProps,
  type ConnectionStatusState,
} from './feedback/ConnectionStatus';
export { SystemStatusIndicator, type SystemStatusEnv } from './feedback/SystemStatusIndicator';

// =============================================================================
// AGENTS (brand face empty / loading)
// =============================================================================

export {
  AgentEmptyState,
  type AgentEmptyStateProps,
  type AgentEmptyStateAction,
  type AgentEmptyStateVariant,
  type AgentEmptyPose,
} from './agents';

// =============================================================================
// DESK CHASSIS (Ant + tokens — use on every instrument surface)
// =============================================================================

export {
  DeskChassis,
  DESK_EMPTY,
  DeskButton,
  DeskInput,
  DeskSelect,
  DeskSwitch,
  DeskSegmented,
  DeskTabs,
  DeskReadout,
  DeskReadoutRow,
  DeskChip,
  DeskSideChip,
  DeskStatus,
} from './desk'

// =============================================================================
// DESK WIDGETS (operational — dashboard, portfolio, trading, agents)
// =============================================================================

export {
  WidgetChrome,
  widgetChromeStyles,
  EquitySummaryWidget,
  RiskSummaryWidget,
  AgentSupervisionWidget,
  MetricStatGridWidget,
  PositionsTableWidget,
  OrdersTableWidget,
  DataFeedReadoutWidget,
  OrderEntryWidget,
  formatSignedCurrency,
  formatSignedPercent,
  formatQty,
  resolvePositionSide,
  resolvePositionStatus,
  tradeDeskHref,
  STALE_MS,
  toWidgetPosition,
  toWidgetPositions,
  type EquitySummaryWidgetProps,
  type RiskSummaryWidgetProps,
  type AgentSupervisionWidgetProps,
  type MetricStatGridWidgetProps,
  type PositionsTableWidgetProps,
  type OrdersTableWidgetProps,
  type DataFeedReadoutWidgetProps,
  type OrderEntryWidgetProps,
  type OrderEntryPayload,
  type DeskFeedStatus,
  type EquitySummaryValues,
  type RiskSummaryValues,
  type AgentSupervisionCounts,
  type DeskMetricItem,
  type PositionSide,
} from './widgets';

// =============================================================================
// LIBRARIES (Advanced+ grid/DCA deploy-as-agent)
// =============================================================================

export {
  DeployAsAgentButton,
  type DeployAsAgentButtonProps,
  type DeployAsAgentMode,
  type LibraryTemplateKind,
  GridLibrary,
  type GridLibraryProps,
  DcaLibrary,
  type DcaLibraryProps,
  GRID_TEMPLATES,
  getGridTemplate,
  listGridTemplates,
  type GridTemplate,
  type GridTemplateId,
  type GridTemplateDefaults,
  DCA_TEMPLATES,
  getDcaTemplate,
  listDcaTemplates,
  type DcaTemplate,
  type DcaTemplateId,
  type DcaTemplateDefaults,
} from './libraries';

// =============================================================================
// INPUTS
// =============================================================================

export { Switch } from './inputs/Switch';
export { ThemeSwitcher } from './inputs/ThemeSwitcher';

// =============================================================================
// TYPOGRAPHY
// =============================================================================

export { Typography, SafeTypography } from './typography/SafeTypography';
export { ClientOnlyTime } from './typography/ClientOnlyTime';

// Placeholder exports for missing components (to be implemented)
export { ShineBorder } from './effects/ShineBorder';
export { useShine, type ShineConfig } from '@/shared/hooks/theme/useShine';

// Placeholder for missing Ant Design icon
export const TrendingUpOutlined = () => null;
